import React, { useState, useEffect } from "react"
import { Card, ListGroup } from "react-bootstrap"
import classnames from "classnames"
import Select from "react-select"
import _ from "lodash"
import { FaCheck, FaTimes } from "react-icons/fa"
import { MdOutlineInfo } from "react-icons/md"
import CustomTooltip from "../components/custom_tooltip"
import CTAButton from "../components/cta_btn"
import * as PricingConstants from "./../helpers/constant"
import { getPriceString } from "../helpers/helpers"

const initialState = {
  selectedPlan: {},
}

const PlanCard = ({ plans, currency, selectedToggle }) => {
  initialState.selectedPlan = _.first(plans)
  const [state, setState] = useState(initialState)
  const [selectedDropdownValue, setSelectedDropdownValue] = useState(
    _.first(plans)
  )

  const isDropdownNeeded = plans.length > 1
  const plan = isDropdownNeeded ? selectedDropdownValue : _.first(plans)

  const isBestValue = _.get(plan, "is_best_seller", false)
  const isCustom = _.get(plan, "is_custom", false)
  const planName = _.get(plan, "group_display_name", "")
  const credits = _.get(plan, "credits", "").toLocaleString(
    PricingConstants.CURRENCY.USD.locale
  )
  const planId = _.get(plan, "_id", "")
  const tooltipText =
    plan && plan.renewal?.unit === "year"
      ? "20% less than One Time"
      : "10% less than One Time"

  useEffect(() => {
    if (plans.length > 0) {
      const selectedPlan = plans.find(
        plan => plan.credits === selectedDropdownValue.credits
      )

      const newSelectedPlan = selectedPlan || _.first(plans)
      setSelectedDropdownValue(newSelectedPlan)
      setState(prevState => ({
        ...prevState,
        selectedPlan: newSelectedPlan,
      }))
    }
  }, [plans, selectedDropdownValue])

  const handlePlanChange = selectedOption => {
    const selectedPlanId = selectedOption.value
    const newSelectedPlan = plans.find(plan => plan._id === selectedPlanId)

    setState(prevState => ({
      ...prevState,
      selectedPlan: newSelectedPlan,
    }))
    setSelectedDropdownValue(newSelectedPlan)
  }

  const dropDownOptions = plans?.map(plan => ({
    value: plan._id,
    label: (
      <div className="d-flex flex-column">
        <div className="d-flex justify-content-end">{plan.short_name}</div>
        <div className="plan-dropdown_options">
          <div className="dropdown-strike-price notranslate">
            {PricingConstants.CURRENCY[currency].symbol}
            {plan.price.one_time.toLocaleString(PricingConstants.CURRENCY[currency].locale)}
          </div>
          &nbsp;
          <div className="dropdown-normal-price">
            <span className="notranslate">
              {PricingConstants.CURRENCY[currency].symbol}
              {plan.price.per_month.toLocaleString(
                PricingConstants.CURRENCY[currency].locale
              )}
            </span>
            <span className="month">/month</span>
          </div>
        </div>
      </div>
    ),
  }))

  const getStrickedAndNormalPrice = () => {
    if (planId === PricingConstants.FREEMIUM_PRODUCT_ID) {
      return (
        <div className="price-container">
          <div className="normal-price notranslate">
            {getPriceString({ price: plan.price.per_month, currency })}
            <span className="month">/month</span>
          </div>
        </div>
      )
    }

    let showTooltip = true
    if (selectedToggle === 'bfs_2025')
      showTooltip = false

    return (
      <div className="price-container">
        <div className="strike-price notranslate">
          {getPriceString({ price: plan.price.one_time, currency })}
        </div>
        &nbsp;
        {
          showTooltip
            ? <CustomTooltip
              tootipId={`tooltip-${planId}`}
              tooltipText={tooltipText}
            >
              <div
                className="normal-price notranslate"
                id={`tooltip-${planId}`}
              >
                {getPriceString({ price: plan.price.per_month, currency })}
                <span className="month">/month</span>
              </div>
            </CustomTooltip>
            : <div
              className="normal-price notranslate"
              id={planId}
            >
              {getPriceString({ price: plan.price.per_month, currency })}
              <span className="month">/month</span>
            </div>
        }
      </div>
    )
  }

  const getPlanCardHeader = () => {
    return (
      <>
        <div className="header-title">
          {isDropdownNeeded ? (
            <Select
              className="plan-select-container"
              classNamePrefix="plan-select"
              value={{
                value: selectedDropdownValue?._id,
                label: (
                  <span className="plan-name">
                    {state.selectedPlan.short_name}
                  </span>
                ),
              }}
              onChange={handlePlanChange}
              options={dropDownOptions}
              isSearchable={false}
            />
          ) : (
            <div className="plan-name">
              {planName}
              <span>{credits} Credits </span>
            </div>
          )}
          <div className="header-subtitle">{plan.tagline}</div>
        </div>
        {getStrickedAndNormalPrice()}
      </>
    )
  }

  const TooltipList = ({ items }) => {
    return (
      <ul className="tooltip-list">
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    )
  }

  const getTooltip = groupKey => {
    return (
      <CustomTooltip
        tootipId={`tooltip-${planId}`}
        tooltipText={<TooltipList items={plan.groups[groupKey].tooltip} />}
        placement="right"
        className="custom-header-tooltip"
      >
        <span
          className="info-tooltip-icon ml-2"
          data-tip
          data-for={groupKey + "_tooltip"}
          data-iscapture="true"
        >
          <MdOutlineInfo />
        </span>
      </CustomTooltip>
    )
  }

  return (
    <Card
      className={classnames("pricing-card", {
        "card-tag": isBestValue || isCustom,
      })}
    >
      <div className={classnames({
        "card-tag__best-value": plan.badge_text,
        'd-none': !plan.badge_text
      })}
      >
        {plan.badge_text}
      </div>
      <Card.Header
        className={classnames("pricing-card__header", {
          "dropdown-card-header": isDropdownNeeded,
        })}
      >
        {getPlanCardHeader()}
        <CTAButton product={plan} />
        <span className="no-credit-card_text">No Credit Card Required</span>
      </Card.Header>
      <ListGroup>
        <ListGroup.Item>
          <div className="feature-icon-container">
            <FaCheck size={"100%"} />
          </div>

          <CustomTooltip
            tootipId={`expiry_description_tooltip-${planId}`}
            tooltipText={plan.expiry_description_tooltip}
            placement="bottom"
            className="custom-header-tooltip plan-subheading_tooltip"
          >
            <span
              id={`expiry_description_tooltip-${planId}`}
              className="plan-description"
              dangerouslySetInnerHTML={{ __html: plan.description }}
            />
          </CustomTooltip>
        </ListGroup.Item>
        {Object.keys(plan.groups).map(groupKey => (
          <React.Fragment key={groupKey}>
            <ListGroup.Item className="clickable-item">
              {plan.groups[groupKey].display_name}
              {plan.groups[groupKey].tooltip && getTooltip(groupKey)}
            </ListGroup.Item>

            {plan.features
              .filter(feature => feature.group === groupKey)
              .map((feature, index) => {
                const tooltipId = `feature-${planId}-${index}`
                return (
                  <ListGroup.Item
                    key={tooltipId}
                    className={classnames({ "text-muted": !feature.checked })}
                    data-tip
                    data-for={tooltipId}
                    data-iscapture="true"
                  >
                    <div className="feature-icon-container">
                      {feature.checked ? (
                        <FaCheck size={"100%"} />
                      ) : (
                        <FaTimes size={"100%"} />
                      )}
                    </div>

                    {feature.tooltip ? (
                      <CustomTooltip
                        tootipId={tooltipId}
                        tooltipText={feature.tooltip}
                        placement="right"
                        className="custom-header-tooltip plan-subheading_tooltip"
                      >
                        <span id={tooltipId} className="wavy-underline">
                          {feature.description}
                        </span>
                      </CustomTooltip>
                    ) : (
                      <span>{feature.description}</span>
                    )}
                  </ListGroup.Item>
                )
              })}
          </React.Fragment>
        ))}
      </ListGroup>
    </Card>
  )
}

export default PlanCard
